import ApisServiceApp from './ApisServiceApp'
import { postMultipartFile } from '@/utils/blobApiUtils'
import { apiRequest } from '@/utils/apiRequestUtils'
import { banHangListParamsFromQueryString } from '@/utils/banHangListQueryUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import type { IKiemKe, IKiemKeLine, IKiemKePayload, IKiemKeLinePayload } from '@/interfaces/kiemKeInterfaces'
import { normalizeKiemKeTrangThai } from '@/interfaces/kiemKeInterfaces'
import { maGocTuHangHoa } from '@/utils/maHangHienThi'

/** Module Kiểm kê — [BanHang API Docs](https://banhang-dev.letrangroup.vn/api-docs#/Kiểm%20Kê) */
const PREFIX = '/kiem-ke'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yeu cau that bai')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yeu cau that bai')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

const parseNum = (raw: unknown): number | null => {
  if (raw === '' || raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const mapLine = (raw: Record<string, unknown>): IKiemKeLine => {
  const hk = raw.hangHoa
  const hkO = hk && typeof hk === 'object' ? (hk as Record<string, unknown>) : undefined
  return {
    id: raw.id != null ? String(raw.id) : undefined,
    hangHoaId: String(raw.hangHoaId ?? hkO?.id ?? ''),
    maHangHoa: raw.maHangHoa != null ? String(raw.maHangHoa) : hkO?.maHangHoa != null ? String(hkO.maHangHoa) : null,
    hangHoaGocMa: maGocTuHangHoa(hkO),
    tenHangHoa: raw.tenHangHoa != null ? String(raw.tenHangHoa) : hkO?.tenHangHoa != null ? String(hkO.tenHangHoa) : null,
    donViTinh: raw.donViTinh != null ? String(raw.donViTinh) : null,
    tonKho: parseNum(raw.tonKho) ?? 0,
    soLuongThucTe: parseNum(raw.soLuongThucTe),
    soLuongLech: parseNum(raw.soLuongLech) ?? 0,
    giaVon: parseNum(raw.giaVon) ?? 0,
    giaTriLech: parseNum(raw.giaTriLech) ?? 0,
    serialImei: raw.serialImei != null ? String(raw.serialImei) : null,
    dateSanPham: raw.dateSanPham != null ? String(raw.dateSanPham) : null,
    lyDoKiemKe: raw.lyDoKiemKe != null ? String(raw.lyDoKiemKe) : null,
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
  }
}

export const mapKiemKeFromApi = (raw: Record<string, unknown>): IKiemKe => {
  const { trangThai, trangThaiRaw } = normalizeKiemKeTrangThai(raw.trangThai)
  const chiTietRaw = raw.chiTiets ?? raw.danhSachHangHoa
  const lines = Array.isArray(chiTietRaw)
    ? chiTietRaw.map((r) => mapLine((r && typeof r === 'object' ? r : {}) as Record<string, unknown>))
    : []
  const nguoiTao = raw.nguoiTao
  const nguoiTaoO = nguoiTao && typeof nguoiTao === 'object' ? (nguoiTao as Record<string, unknown>) : undefined
  const chiNhanh = raw.chiNhanh
  const chiNhanhO = chiNhanh && typeof chiNhanh === 'object' ? (chiNhanh as Record<string, unknown>) : undefined
  return {
    id: String(raw.id ?? ''),
    maPhieuKiem: String(raw.maPhieuKiem ?? ''),
    thoiGian: String(raw.thoiGian ?? ''),
    ngayCanBang: raw.ngayCanBang != null ? String(raw.ngayCanBang) : null,
    trangThai,
    trangThaiRaw,
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    tongSlThucTe: parseNum(raw.tongSlThucTe),
    tongChenhLech: parseNum(raw.tongChenhLech),
    tongGiaTriThucTe: parseNum(raw.tongGiaTriThucTe),
    slLechTang: parseNum(raw.slLechTang),
    slLechGiam: parseNum(raw.slLechGiam),
    danhSachHangHoa: lines,
    nguoiTaoId:
      raw.nguoiTaoId != null
        ? String(raw.nguoiTaoId)
        : nguoiTaoO?.id != null
          ? String(nguoiTaoO.id)
          : null,
    nguoiTaoLabel:
      nguoiTaoO?.fullName != null && String(nguoiTaoO.fullName).trim()
        ? String(nguoiTaoO.fullName)
        : nguoiTaoO?.username != null
          ? String(nguoiTaoO.username)
          : null,
    chiNhanhId:
      raw.chiNhanhId != null
        ? String(raw.chiNhanhId)
        : chiNhanhO?.id != null
          ? String(chiNhanhO.id)
          : null,
    chiNhanhTen:
      chiNhanhO?.ten != null && String(chiNhanhO.ten).trim()
        ? String(chiNhanhO.ten)
        : null,
    phieuKhos: Array.isArray(raw.phieuKhos)
      ? (raw.phieuKhos as Array<Record<string, unknown>>)
          .filter((p) => p?.id && p?.maPhieu)
          .map((p) => ({
            id: String(p.id),
            maPhieu: String(p.maPhieu),
            loaiPhieu: p.loaiPhieu != null ? String(p.loaiPhieu) : undefined,
            huong: p.huong != null ? String(p.huong) : undefined,
            trangThai: p.trangThai != null ? String(p.trangThai) : undefined,
          }))
      : [],
  }
}

export const toKiemKePayload = (body: IKiemKePayload): Record<string, unknown> => ({
  ...(body.maPhieuKiem?.trim() ? { maPhieuKiem: body.maPhieuKiem.trim() } : {}),
  ...(body.thoiGian ? { thoiGian: body.thoiGian } : {}),
  ...(body.ghiChu?.trim() ? { ghiChu: body.ghiChu.trim() } : {}),
  ...(body.chiNhanhId != null && Number.isFinite(Number(body.chiNhanhId))
    ? { chiNhanhId: Number(body.chiNhanhId) }
    : {}),
  ...(body.danhSachHangHoa?.length
    ? {
        danhSachHangHoa: body.danhSachHangHoa.map((x: IKiemKeLinePayload) => ({
          hangHoaId: x.hangHoaId,
          ...(x.soLuongThucTe != null ? { soLuongThucTe: Number(x.soLuongThucTe) } : {}),
          ...(x.donViTinh?.trim() ? { donViTinh: x.donViTinh.trim() } : {}),
          ...(x.serialImei?.trim() ? { serialImei: x.serialImei.trim() } : {}),
          ...(x.dateSanPham?.trim() ? { dateSanPham: x.dateSanPham.trim() } : {}),
          ...(x.lyDoKiemKe?.trim() ? { lyDoKiemKe: x.lyDoKiemKe.trim() } : {}),
          ...(x.ghiChu?.trim() ? { ghiChu: x.ghiChu.trim() } : {}),
        })),
      }
    : {}),
})

export const fetchKiemKeList = (search: string): Promise<PagedListResult<IKiemKe>> =>
  fetchDanhSachPhieuKiemKe(search).then((res) => ({
    data: res.data.map((r) => mapKiemKeFromApi(r)),
    paging: res.paging,
    summary: res.summary,
  }))

export const fetchKiemKeDetail = async (id: string | number): Promise<IKiemKe> => {
  const res = await fetchChiTietPhieuKiemKe(id)
  const raw = res.data
  return mapKiemKeFromApi(raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {})
}

export const taoKiemKe = (body: IKiemKePayload) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/tao-phieu-kiem-ke`, toKiemKePayload(body)))

export const capNhatKiemKe = (id: string | number, body: IKiemKePayload) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/cap-nhat-phieu-kiem-ke`, toKiemKePayload(body)))

export type PreviewTonKiemKeItem = {
  hangHoaId: string
  maHangHoa?: string
  tenHangHoa?: string
  donViCoBan?: string
  tonKho: number
  giaVon: number
  giaTriTon: number
}

/** Preview tồn sổ + đơn giá vốn (theo sổ PKCT trước `thoiGian` nếu có — như thẻ kho). */
export const previewTonKiemKe = async (
  hangHoaIds: string[],
  thoiGian?: string | null,
  chiNhanhId?: number | string | null,
): Promise<PreviewTonKiemKeItem[]> => {
  const ids = [...new Set(hangHoaIds.map((id) => String(id).trim()).filter(Boolean))]
  if (!ids.length) return []
  const params: Record<string, string> = { hangHoaIds: ids.join(',') }
  const tg = thoiGian != null ? String(thoiGian).trim() : ''
  if (tg) params.thoiGian = new Date(tg).toISOString()
  const cn = chiNhanhId != null && String(chiNhanhId).trim() ? Number(chiNhanhId) : NaN
  if (Number.isFinite(cn)) params.chiNhanhId = String(cn)
  const res = await apiRequest(ApisServiceApp.get(`${PREFIX}/preview-ton-kiem-ke`, { params }))
  const raw = normalizeDetailResponse(res)
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)
      ? ((raw as { data: unknown[] }).data)
      : []
  return list.map((row) => {
    const o = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>
    return {
      hangHoaId: String(o.hangHoaId ?? ''),
      maHangHoa: o.maHangHoa != null ? String(o.maHangHoa) : undefined,
      tenHangHoa: o.tenHangHoa != null ? String(o.tenHangHoa) : undefined,
      donViCoBan: o.donViCoBan != null ? String(o.donViCoBan) : undefined,
      tonKho: parseNum(o.tonKho) ?? 0,
      giaVon: parseNum(o.giaVon) ?? 0,
      giaTriTon: parseNum(o.giaTriTon) ?? 0,
    }
  })
}

const kiemKeListParamsFromQueryString = (search: string): Record<string, unknown> => {
  const params = banHangListParamsFromQueryString(search)
  if (params.nguoiTao != null && params.nguoiTaoId == null) {
    params.nguoiTaoId = params.nguoiTao
    delete params.nguoiTao
  }
  if (params.sort != null && params.sortField == null) {
    params.sortField = params.sort
    delete params.sort
  }
  if (params.sortDir != null && params.sortOrder == null) {
    params.sortOrder = params.sortDir
    delete params.sortDir
  }
  if (typeof params.trangThai === 'string' && params.trangThai.includes(',')) {
    params.trangThai = params.trangThai
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return params
}

export const fetchDanhSachPhieuKiemKe = (
  search: string,
): Promise<PagedListResult<Record<string, unknown>>> => {
  const params = kiemKeListParamsFromQueryString(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-phieu-kiem-ke`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data as Record<string, unknown>[], paging, summary }
    }),
  )
}

/** @deprecated Dùng `fetchKiemKeDetail` */
export const taoPhieuKiemKe = (body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/tao-phieu-kiem-ke`, body))

export const fetchChiTietPhieuKiemKe = (id: string | number) =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-phieu-kiem-ke`).then((res: unknown) => ({
      data: normalizeDetailResponse(res as { data?: Record<string, unknown> }),
    })),
  )

/** @deprecated Dùng `capNhatKiemKe` */
export const capNhatPhieuKiemKe = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/cap-nhat-phieu-kiem-ke`, body))

export type PhanBoLoKiemKe = {
  hangHoaId: string
  lo: Array<{ maLo: string; hanSuDung?: string; soLuong: number }>
}

export const hoanThanhPhieuKiemKe = (
  id: string | number,
  body?: { thoiGian?: string | null; phanBoLo?: PhanBoLoKiemKe[] },
) => {
  const payload: { thoiGian?: string; phanBoLo?: PhanBoLoKiemKe[] } = {}
  const thoiGian = body?.thoiGian != null ? String(body.thoiGian).trim() : ''
  if (thoiGian) payload.thoiGian = thoiGian
  if (body?.phanBoLo?.length) payload.phanBoLo = body.phanBoLo
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/hoan-thanh-phieu-kiem-ke`, payload))
}

export const huyPhieuKiemKe = (id: string | number) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/huy-phieu-kiem-ke`, {}))

export const xoaPhieuKiemKe = (id: string | number) =>
  apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-phieu-kiem-ke`))

export const themHangHoaPhieuKiemKe = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/them-hang-hoa`, body))

export const xoaHangHoaPhieuKiemKe = (id: string | number, hangHoaId: string) =>
  apiRequest(
    ApisServiceApp.delete(`${PREFIX}/${id}/xoa-hang-hoa`, { params: { hangHoaId } }),
  )

export const capNhatSoLuongThucTePhieuKiemKe = (
  id: string | number,
  body: Record<string, unknown>,
) => apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/cap-nhat-so-luong-thuc-te`, body))

export const saoChepPhieuKiemKe = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/sao-chep-phieu-kiem-ke`, {}))

export const importExcelPhieuKiemKe = (id: string | number, file: File) =>
  apiRequest(postMultipartFile(`${PREFIX}/${id}/nhap-hang-hoa-tu-excel-phieu-kiem-ke`, file))

export const xuatPhieuKiemKeTongQuanBlob = async (search?: string): Promise<Blob> => {
  const params = search ? kiemKeListParamsFromQueryString(search) : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-tong-quan-phieu-kiem-ke`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const xuatPhieuKiemKeChiTietBlob = async (id: string | number): Promise<Blob> => {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/${id}/xuat-file-chi-tiet-phieu-kiem-ke`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const taiTemplateImportKiemKeBlob = async (): Promise<Blob> => {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/tai-template-import-phieu-kiem-ke`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}
